import {
  ReactElement,
  RefObject,
  UIEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'

import {
  AnimatedCopyButton,
  ChevronRightIcon,
  PlusIconCorrect,
  RefreshIcon,
  RenderIf,
} from 'components'
import {selectUser} from 'features/User'
import {DopamineHistoryMessage} from 'interfaces'
import {DopamineAssistantComposer} from './DopamineAssistantComposer'
import {DopamineAssistantGuide} from './DopamineAssistantGuide'
import {DopamineAssistantProgress} from './DopamineAssistantProgress'
import {DopamineAssistantResult} from './DopamineAssistantResult'
import {DopamineAssistantMark} from './DopamineAssistantMark'
import {DopamineAssistantSuggestionIcon} from './DopamineAssistantSuggestionIcon'
import {
  DopamineAssistantSessionController,
  DopamineAssistantTurn,
  DopaminePromptSubmission,
  DopaminePromptSuggestion,
  DopamineRunOutcome,
  DopamineRunProgress,
  DopamineRunState,
} from './DopamineAssistant.types'
import {
  getDopamineViewConfig,
  normalizeDopaminePathname,
} from './DopamineAssistant.viewConfig'
import {getDopamineFollowUps} from './DopamineAssistant.utils'
import {useDopamineAssistantRun} from './hooks'

/* In full screen the content sits in a centred reading column while the
   scroll area itself spans the panel, so the scrollbar stays at the edge. */
const DOPAMINE_FULLSCREEN_GUTTER_CLASSES =
  'group-data-[fullscreen=true]/dopamine:px-6'
const DOPAMINE_FULLSCREEN_COLUMN_CLASSES =
  'group-data-[fullscreen=true]/dopamine:mx-auto group-data-[fullscreen=true]/dopamine:w-full group-data-[fullscreen=true]/dopamine:max-w-dopamine-column 5xl:group-data-[fullscreen=true]/dopamine:max-w-dopamine-column-wide'

const SCROLL_FOLLOW_THRESHOLD_PX = 32

const getGreetingKey = (): string => {
  const hour = new Date().getHours()
  if (hour < 12) return 'GOOD_MORNING'
  if (hour < 18) return 'GOOD_AFTERNOON'
  return 'GOOD_EVENING'
}

type ConversationView = 'loading' | 'conversation' | 'empty' | 'error'

type DopamineHistorySegment =
  | {id: string; message: DopamineHistoryMessage; type: 'user'}
  | {
      id: string
      messages: DopamineHistoryMessage[]
      question: string
      type: 'response'
    }

const groupHistoryMessages = (
  messages: DopamineHistoryMessage[],
): DopamineHistorySegment[] =>
  messages.reduce<DopamineHistorySegment[]>((segments, message) => {
    if (message.role === 'user') {
      segments.push({id: message.id, message, type: 'user'})
      return segments
    }

    const currentSegment = segments.at(-1)
    if (currentSegment?.type === 'response') {
      currentSegment.messages.push(message)
      return segments
    }

    segments.push({
      id: message.id,
      messages: [message],
      question:
        currentSegment?.type === 'user' ? currentSegment.message.content : '',
      type: 'response',
    })
    return segments
  }, [])

const createCompletedHistoryProgress = (
  messages: DopamineHistoryMessage[],
): DopamineRunProgress => ({
  endedAt: null,
  failureMessage: null,
  items: messages.map(message => ({
    activeLabel: message.content,
    completedLabel: message.content,
    id: message.id,
    kind: 'tool',
    status: 'completed',
  })),
  runId: `history:${messages.map(({id}) => id).join(':')}`,
  startedAt: null,
  status: 'completed',
})

const getHistoryCopyValue = (messages: DopamineHistoryMessage[]): string =>
  messages.reduce(
    (copyValue, message) =>
      message.role === 'assistant' && message.content.trim()
        ? message.content
        : copyValue,
    '',
  )

const getRunOutcome = (
  turns: DopamineAssistantTurn[],
): DopamineRunOutcome | null => {
  const lastTurn = turns.at(-1)
  if (lastTurn?.status === 'completed') return 'answer'
  if (lastTurn?.status === 'failed') return 'failed'
  return null
}

// The composer is hidden for read-only or loading chats and disabled while
// history is failing or refreshing; the guide's example needs it usable.
const getComposerAvailability = (
  session: DopamineAssistantSessionController,
): {canFill: boolean; isDisabled: boolean; isShown: boolean} => {
  const isShown = !session.isReadOnly && !session.isHistoryLoading
  const isDisabled = session.isHistoryError || session.isHistoryRefreshing
  return {canFill: isShown && !isDisabled, isDisabled, isShown}
}

const getConversationView = (
  isHistoryLoading: boolean,
  hasVisibleConversation: boolean,
  isHistoryError: boolean,
): ConversationView => {
  if (isHistoryLoading) return 'loading'
  if (isHistoryError) return 'error'
  if (hasVisibleConversation) return 'conversation'
  return 'empty'
}

const AssistantMessageHeader = ({
  isRunning = false,
}: {
  isRunning?: boolean
}): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})

  return (
    <div className="dopamine-ai-message-header flex items-center gap-2 pl-px">
      <span className="dopamine-ai-message-avatar flex h-[1.4375rem] w-[1.4375rem] shrink-0 items-center justify-center rounded-[0.4375rem] text-white">
        <DopamineAssistantMark
          animated={isRunning}
          className="h-[0.9375rem] w-[0.9375rem]"
        />
      </span>
      {/* The header already names Dopamine; the mark alone tags the answer,
          with the name kept for screen readers. */}
      <span className="sr-only">{t('DOPAMINE')}</span>
    </div>
  )
}

type DopamineAssistantConversationProps = {
  composerRef: RefObject<HTMLTextAreaElement>
  isGuideOpen?: boolean
  isVisible?: boolean
  onCloseGuide?(): void
  onDismissGuide?(): void
  onRunStateChange?(state: DopamineRunState): void
  onStartNewChat(): void
  session: DopamineAssistantSessionController
}

export const DopamineAssistantConversation = ({
  composerRef,
  isGuideOpen = false,
  isVisible = true,
  onCloseGuide,
  onDismissGuide,
  onRunStateChange,
  onStartNewChat,
  session,
}: DopamineAssistantConversationProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const {name} = useSelector(selectUser)
  const {pathname} = useLocation()
  const normalizedPathname = normalizeDopaminePathname(pathname)
  const [draft, setDraft] = useState('')
  // Show the shortcut while the user is reading away from the latest update.
  const [isAwayFromLatest, setIsAwayFromLatest] = useState(false)
  const conversationRef = useRef<HTMLElement>(null)
  const retrySubmissionLockRef = useRef(false)
  const shouldFollowConversationRef = useRef(true)
  const settledHistoriesRef = useRef(
    new Map<string, DopamineHistoryMessage[]>(),
  )
  const settledHistory =
    settledHistoriesRef.current.get(session.clientSessionId) ?? session.history
  const {
    isRunning,
    queuedTurn,
    removeQueuedTurn,
    submitPrompt: queuePrompt,
    turns,
  } = useDopamineAssistantRun({
    clientSessionId: session.clientSessionId,
    onSessionExpired: session.markSessionReadOnly,
    onSessionSettled: session.markSessionSettled,
    onSessionStarted: session.markSessionStarted,
  })
  const displayName = name.trim().split(/\s+/)[0] || t('THERE')
  const viewConfig = getDopamineViewConfig(pathname)
  const suggestions: DopaminePromptSuggestion[] = viewConfig.suggestions.map(
    ({key, icon}) => ({
      displayText: t(`${key}_TITLE`),
      description: t(`${key}_DESCRIPTION`),
      id: key,
      query: t(`${key}_QUERY`),
      icon,
    }),
  )

  useLayoutEffect(() => {
    shouldFollowConversationRef.current = true
    setDraft('')
    setIsAwayFromLatest(false)
  }, [session.clientSessionId, session.conversationRevision])

  useLayoutEffect(() => {
    const conversation = conversationRef.current
    if (!conversation || !isVisible) return

    if (!shouldFollowConversationRef.current) {
      const distanceFromBottom =
        conversation.scrollHeight -
        conversation.scrollTop -
        conversation.clientHeight
      setIsAwayFromLatest(distanceFromBottom > SCROLL_FOLLOW_THRESHOLD_PX)
      return
    }

    conversation.scrollTop = Math.max(
      0,
      conversation.scrollHeight - conversation.clientHeight,
    )
  }, [
    isVisible,
    normalizedPathname,
    session.clientSessionId,
    session.conversationRevision,
    session.history.length,
    turns,
  ])

  const handleConversationScroll = (event: UIEvent<HTMLElement>): void => {
    const {clientHeight, scrollHeight, scrollTop} = event.currentTarget
    const isNearBottom =
      scrollHeight - scrollTop - clientHeight <= SCROLL_FOLLOW_THRESHOLD_PX
    shouldFollowConversationRef.current = isNearBottom
    setIsAwayFromLatest(!isNearBottom)
  }

  const scrollToLatest = (): void => {
    const conversation = conversationRef.current
    shouldFollowConversationRef.current = true
    setIsAwayFromLatest(false)
    if (conversation) {
      conversation.scrollTop = Math.max(
        0,
        conversation.scrollHeight - conversation.clientHeight,
      )
    }
  }

  const submitPrompt = (submission: DopaminePromptSubmission): void => {
    if (
      session.isReadOnly ||
      session.isHistoryError ||
      session.isHistoryRefreshing
    ) {
      return
    }

    shouldFollowConversationRef.current = true
    setIsAwayFromLatest(false)
    // A question sent from under the guide is the user moving on to the chat.
    onDismissGuide?.()
    session.markSessionSubmitted(submission.displayText)
    queuePrompt(submission)
  }

  const hasPendingTurn = turns.some(
    turn =>
      turn.status === 'active' ||
      turn.status === 'queued' ||
      turn.status === 'submitting',
  )
  const visibleHistory = hasPendingTurn ? settledHistory : session.history
  const historyMessageIds = new Set(visibleHistory.map(message => message.id))
  const visibleTurns = turns.filter(
    turn =>
      turn.status !== 'completed' ||
      !turn.answer.messageId ||
      !historyMessageIds.has(turn.answer.messageId),
  )
  const historySegments = groupHistoryMessages(visibleHistory)
  const hasVisibleConversation =
    historySegments.length > 0 || visibleTurns.length > 0
  const isVisibleHistoryError = session.isHistoryError && !hasPendingTurn
  const isRetryDisabled =
    hasPendingTurn || isVisibleHistoryError || session.isHistoryRefreshing

  useLayoutEffect(() => {
    if (!hasPendingTurn) {
      settledHistoriesRef.current.set(session.clientSessionId, session.history)
    }
  }, [hasPendingTurn, session.clientSessionId, session.history])

  useEffect(() => {
    if (!isRunning) retrySubmissionLockRef.current = false
  }, [isRunning])

  // Tell the shell when work starts and how it ended, so the rail can show
  // it while the panel is collapsed. Only transitions are reported.
  const previousIsRunningRef = useRef(isRunning)
  useEffect(() => {
    if (previousIsRunningRef.current === isRunning) return

    previousIsRunningRef.current = isRunning
    onRunStateChange?.({
      isRunning,
      outcome: isRunning ? null : getRunOutcome(turns),
    })
  }, [isRunning, onRunStateChange, turns])

  const retryPrompt = (submission: DopaminePromptSubmission): void => {
    if (retrySubmissionLockRef.current || isRetryDisabled) return

    retrySubmissionLockRef.current = true
    submitPrompt(submission)
  }

  const conversationView = getConversationView(
    session.isHistoryLoading,
    hasVisibleConversation,
    isVisibleHistoryError,
  )

  const composer = getComposerAvailability(session)

  // The guide's example lands in the composer to edit or send; it is not sent.
  const tryGuideExample = (question: string): void => {
    setDraft(question)
    composerRef.current?.focus()
  }

  const submitSuggestion = (suggestion: DopaminePromptSuggestion): void => {
    setDraft('')
    submitPrompt(suggestion)
  }

  // Follow-ups come from the last answer's own "Next:" steps.
  const lastVisibleTurn = visibleTurns.at(-1)
  const lastAnswerMarkdown =
    lastVisibleTurn?.status === 'completed'
      ? lastVisibleTurn.answer.markdown
      : getHistoryCopyValue(visibleHistory)
  // A failed last turn offers nothing to follow up on; the earlier answer's
  // next steps would read as if they belonged to the failure.
  const followUps =
    hasPendingTurn ||
    session.isReadOnly ||
    session.isHistoryRefreshing ||
    (lastVisibleTurn !== undefined && lastVisibleTurn.status !== 'completed')
      ? []
      : getDopamineFollowUps(lastAnswerMarkdown)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* The guide covers the conversation without unmounting it, so the chat
          keeps its scroll position; only the guide is visible meanwhile. */}
      <div
        className={classNames('relative min-h-0 flex-1 overflow-hidden', {
          invisible: isGuideOpen,
        })}
      >
        <RenderIf isTrue={isGuideOpen}>
          <div
            className={classNames(
              'visible absolute inset-0 z-10 overflow-y-auto',
              DOPAMINE_FULLSCREEN_GUTTER_CLASSES,
            )}
          >
            <div className={DOPAMINE_FULLSCREEN_COLUMN_CLASSES}>
              <DopamineAssistantGuide
                canTryExample={composer.canFill}
                onBack={() => onCloseGuide?.()}
                onTryExample={tryGuideExample}
              />
            </div>
          </div>
        </RenderIf>
        <RenderIf isTrue={conversationView === 'loading'}>
          <div
            className="flex h-full items-center justify-center px-4"
            data-cy="dopamine-assistant-loading"
          >
            <output className="text-center text-xs text-gray-500">
              {t('DOPAMINE_LOADING_CHAT')}
            </output>
          </div>
        </RenderIf>
        <RenderIf isTrue={conversationView === 'conversation'}>
          <section
            aria-label={t('DOPAMINE_CONVERSATION')}
            className={classNames(
              'h-full overflow-y-auto px-3 py-4',
              DOPAMINE_FULLSCREEN_GUTTER_CLASSES,
            )}
            ref={conversationRef}
            onScroll={handleConversationScroll}
          >
            {/* The section scrolls at the panel edge; only its content narrows
                to the reading column in full screen. */}
            <div
              className={classNames(
                'flex flex-col gap-1',
                DOPAMINE_FULLSCREEN_COLUMN_CLASSES,
              )}
            >
              {historySegments.map(segment => {
                if (segment.type === 'user') {
                  return (
                    <p
                      className="dopamine-user-message max-w-dopamine-message self-end whitespace-pre-wrap break-words text-sm leading-5.5 text-white group-data-[fullscreen=true]/dopamine:max-w-dopamine-message-wide"
                      key={segment.id}
                    >
                      {segment.message.content}
                    </p>
                  )
                }

                const copyValue = getHistoryCopyValue(segment.messages)
                const progressMessages = segment.messages.filter(
                  message => message.role === 'tool_call' && message.content,
                )
                const firstProgressMessageId = progressMessages[0]?.id

                return (
                  <div
                    className="mb-3 mt-3 flex w-full flex-col items-stretch gap-2"
                    key={segment.id}
                  >
                    <AssistantMessageHeader />
                    <div className="dopamine-message-surface dopamine-ai-message-surface rounded-[0.875rem] rounded-tl-[0.3125rem] px-[0.9375rem] py-3.5 text-[#f4f4f6] group-data-[fullscreen=true]/dopamine:px-0">
                      {segment.messages.map(message => {
                        if (message.role === 'assistant') {
                          return (
                            <div className="mt-4 first:mt-0" key={message.id}>
                              <DopamineAssistantResult
                                question={segment.question}
                                answer={{
                                  markdown: message.content,
                                  messageId: message.id,
                                  runId: session.clientSessionId,
                                  status: 'completed',
                                }}
                                embedded
                                showActions={false}
                              />
                            </div>
                          )
                        }

                        if (
                          message.role === 'tool_call' &&
                          message.id === firstProgressMessageId
                        ) {
                          return (
                            <div className="mt-4 first:mt-0" key={message.id}>
                              <DopamineAssistantProgress
                                progress={createCompletedHistoryProgress(
                                  progressMessages,
                                )}
                              />
                            </div>
                          )
                        }

                        return null
                      })}
                      {copyValue && (
                        <div className="mt-3 flex items-center gap-1.5 border-t-0.5 border-white/5 pt-2.5">
                          <AnimatedCopyButton
                            ariaLabel={t('DOPAMINE_COPY_ANSWER')}
                            className="dopamine-answer-copy m-0 h-4 w-4"
                            dataCy={`dopamine-assistant-copy-${segment.id}`}
                            size="md"
                            tone="neutral"
                            value={copyValue}
                            variant="default"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
              {visibleTurns.map(turn =>
                turn.status === 'queued' ? null : (
                  <div className="flex flex-col" key={turn.id}>
                    <p className="dopamine-user-message max-w-dopamine-message self-end whitespace-pre-wrap break-words text-sm leading-5.5 text-white group-data-[fullscreen=true]/dopamine:max-w-dopamine-message-wide">
                      {turn.submission.displayText}
                    </p>
                    <div className="mb-3 mt-3 flex w-full flex-col items-stretch gap-2">
                      <AssistantMessageHeader
                        isRunning={
                          turn.status === 'active' ||
                          turn.status === 'submitting'
                        }
                      />
                      <div className="dopamine-message-surface dopamine-ai-message-surface rounded-[0.875rem] rounded-tl-[0.3125rem] px-[0.9375rem] py-3.5 text-[#f4f4f6] group-data-[fullscreen=true]/dopamine:px-0">
                        {turn.status === 'submitting' ? (
                          <output className="text-11px text-dopamine-muted">
                            {t('INVESTIGATING')}
                          </output>
                        ) : (
                          <DopamineAssistantProgress progress={turn.progress} />
                        )}
                        <DopamineAssistantResult
                          answer={turn.answer}
                          question={turn.submission.displayText}
                          embedded
                          isRetryDisabled={isRetryDisabled}
                          onRetry={
                            turn.status === 'completed' && !session.isReadOnly
                              ? () => retryPrompt(turn.submission)
                              : undefined
                          }
                        />
                        {turn.status === 'failed' && !session.isReadOnly ? (
                          <button
                            aria-label={t('DOPAMINE_RETRY_ANSWER')}
                            className="dopamine-answer-action dopamine-tip dopamine-tip-above relative mt-3 focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent disabled:opacity-50"
                            disabled={isRetryDisabled}
                            data-cy="dopamine-assistant-retry-answer"
                            data-tip={t('DOPAMINE_RETRY_ANSWER')}
                            type="button"
                            onClick={() => retryPrompt(turn.submission)}
                          >
                            <RefreshIcon
                              classes="h-3.5 w-3.5"
                              height={14}
                              strokeColor="currentColor"
                              width={14}
                            />
                            <span>{t('RETRY')}</span>
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ),
              )}
              {followUps.length > 0 ? (
                <div
                  aria-label={t('DOPAMINE_FOLLOW_UPS')}
                  className="mt-1 flex flex-wrap gap-1.5 px-px"
                  role="group"
                >
                  {followUps.map(followUp => (
                    <button
                      className="inline-flex max-w-full items-center gap-1.5 rounded-ds-pill border-0.5 border-white/15 bg-white/3 px-3 py-1.5 text-left text-xs leading-snug text-white/85 transition-colors duration-base motion-reduce:transition-none hover:border-dopamine-accent/50 hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent disabled:cursor-not-allowed disabled:opacity-50"
                      data-cy="dopamine-assistant-follow-up"
                      disabled={isRetryDisabled}
                      key={followUp}
                      type="button"
                      onClick={() =>
                        submitPrompt({displayText: followUp, query: followUp})
                      }
                    >
                      <ChevronRightIcon
                        classes="shrink-0 text-dopamine-accent"
                        height={10}
                        strokeColor="currentColor"
                        strokeWidth={1.5}
                        width={6}
                      />
                      <span className="truncate">{followUp}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </section>
        </RenderIf>
        {isAwayFromLatest ? (
          <button
            className="absolute bottom-3 left-1/2 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-ds-pill border-0.5 border-white/15 bg-dopamine-header/90 text-white shadow-lg backdrop-blur-sm transition-colors duration-base motion-reduce:transition-none hover:border-dopamine-accent/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent"
            aria-label={t('DOPAMINE_SCROLL_TO_LATEST')}
            data-cy="dopamine-assistant-scroll-to-latest"
            type="button"
            onClick={scrollToLatest}
          >
            <ChevronRightIcon
              classes="rotate-90 text-dopamine-accent"
              height={12}
              strokeColor="currentColor"
              strokeWidth={1.5}
              width={7}
            />
          </button>
        ) : null}
        <RenderIf isTrue={conversationView === 'error'}>
          <section
            className="flex h-full flex-col items-center justify-center px-4 text-center"
            role="alert"
          >
            <p className="text-xs text-red-400">
              {t('DOPAMINE_CHAT_LOAD_ERROR')}
            </p>
            {/* Retry keeps the chat you wanted; New chat is the way out. */}
            <div className="mt-3 flex items-center gap-3">
              <button
                className="inline-flex h-7 items-center gap-1.5 rounded-ds-pill border-0.5 border-dopamine-accent/50 bg-dopamine-accent/10 px-3 text-xs text-dopamine-accent transition-colors duration-base motion-reduce:transition-none hover:bg-dopamine-accent/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent"
                data-cy="dopamine-assistant-retry-load"
                type="button"
                onClick={session.retryHistory}
              >
                <RefreshIcon
                  classes="h-3.5 w-3.5"
                  height={14}
                  strokeColor="currentColor"
                  width={14}
                />
                <span>{t('RETRY')}</span>
              </button>
              <button
                className="text-xs font-semibold text-dopamine-muted hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent"
                data-cy="dopamine-assistant-error-new-chat"
                type="button"
                onClick={onStartNewChat}
              >
                {t('DOPAMINE_NEW_CHAT')}
              </button>
            </div>
          </section>
        </RenderIf>
        <RenderIf isTrue={conversationView === 'empty'}>
          <div
            className={classNames('h-full', DOPAMINE_FULLSCREEN_GUTTER_CLASSES)}
          >
            <div
              className={classNames(
                'flex h-full flex-col group-data-[fullscreen=true]/dopamine:justify-center',
                DOPAMINE_FULLSCREEN_COLUMN_CLASSES,
              )}
            >
              <section className="shrink-0 px-4 pb-4 pt-5 group-data-[fullscreen=true]/dopamine:pb-6">
                <p className="mb-3 text-11px font-bold uppercase tracking-[0.15em] text-dopamine-muted">
                  {t(viewConfig.contextKey)}
                </p>
                <h3 className="abc-light text-2xl font-normal leading-[1.15] text-white group-data-[fullscreen=true]/dopamine:text-3xl">
                  {t(getGreetingKey())},{' '}
                  <span className="text-dopamine-accent">{displayName}</span>
                </h3>
                <RenderIf isTrue={!session.isReadOnly}>
                  <p className="mt-2.5 max-w-xs text-sm leading-5.5 text-dopamine-muted group-data-[fullscreen=true]/dopamine:max-w-md">
                    {t(viewConfig.introKey)}
                  </p>
                </RenderIf>
              </section>
              <RenderIf isTrue={!session.isReadOnly}>
                <section
                  aria-labelledby="dopamine-assistant-suggestions"
                  className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 group-data-[fullscreen=true]/dopamine:flex-none"
                >
                  <h4
                    className="mb-2 px-1 text-11px uppercase tracking-widest text-dopamine-muted"
                    id="dopamine-assistant-suggestions"
                  >
                    {t('SUGGESTED_FOR_THIS_VIEW')}
                  </h4>
                  <div className="group-data-[fullscreen=true]/dopamine:grid group-data-[fullscreen=true]/dopamine:grid-cols-3 group-data-[fullscreen=true]/dopamine:gap-3">
                    {suggestions.map(suggestion => (
                      <button
                        className={classNames(
                          'group/suggestion relative flex w-full items-center gap-3 border-b-0.5 border-white/5 bg-transparent px-2 py-3 text-left transition-colors duration-base motion-reduce:transition-none last:border-0 hover:bg-white/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-dopamine-accent disabled:cursor-not-allowed disabled:opacity-50',
                          // Full screen lays the same suggestions out as three cards.
                          'group-data-[fullscreen=true]/dopamine:flex-col group-data-[fullscreen=true]/dopamine:items-start group-data-[fullscreen=true]/dopamine:rounded-ds-xl group-data-[fullscreen=true]/dopamine:border-transparent group-data-[fullscreen=true]/dopamine:bg-white/3 group-data-[fullscreen=true]/dopamine:p-4 group-data-[fullscreen=true]/dopamine:hover:bg-white/5',
                        )}
                        data-cy={`dopamine-assistant-suggestion-${suggestion.id.toLowerCase()}`}
                        disabled={session.isHistoryRefreshing}
                        key={suggestion.id}
                        type="button"
                        onClick={() => submitSuggestion(suggestion)}
                      >
                        <span className="absolute left-0 top-1/2 h-0 w-0.5 -translate-y-1/2 rounded-full bg-dopamine-accent transition-all duration-base group-hover/suggestion:h-6 group-focus-visible/suggestion:h-6 group-data-[fullscreen=true]/dopamine:hidden" />
                        <span className="flex h-4.5 w-4.5 shrink-0 items-center justify-center text-dopamine-accent transition-transform duration-base group-hover/suggestion:scale-110 motion-reduce:transition-none group-data-[fullscreen=true]/dopamine:h-6 group-data-[fullscreen=true]/dopamine:w-6">
                          <DopamineAssistantSuggestionIcon
                            icon={suggestion.icon}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-white transition-transform duration-base group-hover/suggestion:translate-x-0.5 motion-reduce:transition-none group-data-[fullscreen=true]/dopamine:whitespace-normal">
                            {suggestion.displayText}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-dopamine-muted group-data-[fullscreen=true]/dopamine:whitespace-normal">
                            {suggestion.description}
                          </span>
                        </span>
                        <ChevronRightIcon
                          classes="text-gray-500 transition-transform duration-base group-hover/suggestion:translate-x-0.5 group-data-[fullscreen=true]/dopamine:hidden"
                          height={12}
                          strokeColor="currentColor"
                          strokeWidth={1.5}
                          width={7}
                        />
                      </button>
                    ))}
                  </div>
                </section>
              </RenderIf>
            </div>
          </div>
        </RenderIf>
      </div>
      {session.isHistoryRefreshing && !session.isHistoryLoading ? (
        <p aria-live="polite" className="sr-only">
          {t('DOPAMINE_LOADING_CHAT')}
        </p>
      ) : null}
      {isVisibleHistoryError && conversationView !== 'error' ? (
        <p
          className="shrink-0 border-t-0.5 border-white/10 px-4 py-4 text-center text-xs text-red-400"
          role="alert"
        >
          {t('DOPAMINE_CHAT_LOAD_ERROR')}
        </p>
      ) : null}
      {session.isReadOnly ? (
        <fieldset
          aria-labelledby="dopamine-assistant-session-ended"
          className="shrink-0 border-t-0.5 border-white/10 px-4 py-3 text-center"
        >
          <p
            className="text-xs leading-5 text-gray-500"
            id="dopamine-assistant-session-ended"
          >
            {t('DOPAMINE_SESSION_ENDED')}
          </p>
          <button
            className="mt-2.5 inline-flex h-8 items-center gap-1.5 rounded-ds-md border-0.5 border-dopamine-accent/40 bg-dopamine-accent/10 px-4 text-13px text-dopamine-accent transition-colors duration-base motion-reduce:transition-none hover:bg-dopamine-accent/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent"
            data-cy="dopamine-assistant-ended-new-chat"
            type="button"
            onClick={onStartNewChat}
          >
            <PlusIconCorrect
              classes="h-3.5 w-3.5"
              height={14}
              strokeColor="currentColor"
              width={14}
            />
            <span>{t('DOPAMINE_NEW_CHAT')}</span>
          </button>
        </fieldset>
      ) : null}
      {composer.isShown ? (
        <div className={DOPAMINE_FULLSCREEN_GUTTER_CLASSES}>
          <div className={DOPAMINE_FULLSCREEN_COLUMN_CLASSES}>
            <DopamineAssistantComposer
              composerRef={composerRef}
              disabled={composer.isDisabled}
              draft={draft}
              isRunning={isRunning}
              key={session.conversationRevision}
              queuedTurn={queuedTurn}
              onDraftChange={setDraft}
              onRemoveQueued={removeQueuedTurn}
              onSubmit={submitPrompt}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}
